/* global Vue, LOYALTY_LEVELS, LOYALTY_API */

import MemberCard from './components/MemberCard'
import { Notifications, setErrorAndStopLoading, startLoading, stopLoading } from '../apps/components/Notifications'
import Modal from '../apps/components/Modal'
import { AuthService, fetchWithTokenAsync } from '../AuthService'
import { messagesSpecific } from '../i18n/translations'
import { CreateI18n } from '../i18n/create-i18n'

const i18n = CreateI18n(messagesSpecific)

const MemberCardApp = Vue.createApp({
  name: 'MemberCardApp',
  components: { MemberCard, Modal, Notifications },
  template: `
<Notifications />
<div>
  <div v-if="loyaltyId !== 0 && member.displayName !== ''">
    <MemberCard
      :loyaltyId="loyaltyId"
      :memberName="member.displayName"
      :memberSince="member.memberSince"
      :qrCode="member.qrCode"
      @createApplePass="createApplePass"
      @createGooglePass="createGooglePass"
      @enlargeQr="isModalOpen = true" />

    <Modal :isOpen="isModalOpen" @closeModal="isModalOpen = false" :title="member.displayName" class="qr-modal">
      <p class="no-margin">{{ $t('dashboard.level') }}: {{ memberLevelName }}</p>
      <p class="no-margin">{{ $t('dashboard.membershipNumber') }}: {{ loyaltyId }}</p>
      <img :src="member.qrCode" width="400" />
    </Modal>
  </div>
</div>`,
  setup () {
    const loyaltyId = Vue.ref(0)
    const isModalOpen = Vue.ref(false)
    const member = Vue.reactive({
      displayName: '',
      memberSince: null,
      qrCode: '',
    })

    const memberLevelName = Vue.computed(() => {
      return (LOYALTY_LEVELS.find(l => l.level === member.level))?.title
    })

    function download (filename, url) {
      const element = document.createElement('a')
      element.setAttribute('href', url)
      element.setAttribute('download', filename)
      element.style.display = 'none'
      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
    }

    async function createApplePass () {
      startLoading('createApplePass')
      try {
        const response = await fetch('/.netlify/functions/create-apple-pass', {
          method: 'POST',
          body: JSON.stringify({ ...member, levelName: memberLevelName.value }),
        })
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        download('Maistra.pkpass', url)
        setTimeout(() => URL.revokeObjectURL(url), 10000)
        stopLoading('createApplePass')
      } catch (err) {
        setErrorAndStopLoading(err, true)
      }
    }

    async function createGooglePass () {
      startLoading('createGooglePass')
      try {
        const response = await fetch('/.netlify/functions/create-google-pass', {
          method: 'POST',
          body: JSON.stringify({ ...member, levelName: memberLevelName.value }),
        })
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        const url = await response.text()
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.click()
        stopLoading('createGooglePass')
      } catch (err) {
        setErrorAndStopLoading(err, true)
      }
    }

    Vue.watch(loyaltyId, async () => {
      try {
        startLoading('member-card')
        member.displayName = AuthService.getDisplayName()
        const responseQrCode = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/loyaltyprofiles/MyLoyaltyQrCode`, AuthService.accessToken, 'GET')
        if (!responseQrCode?.value || responseQrCode.value.length < 5) {
          throw Error('MyLoyaltyQrCode')
        }
        member.qrCode = `data:image/jpeg;base64,${responseQrCode.value}`

        const responseLoyaltyStatus = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/MyLoyaltyStatus`, AuthService.accessToken, 'GET')
        if (!responseLoyaltyStatus?.loyaltyProfile) {
          throw Error('MyLoyaltyStatus')
        }
        member.memberSince = new Date(responseLoyaltyStatus.loyaltyProfile.createdOn)
        member.level = responseLoyaltyStatus.loyaltyProfile.levelId
        stopLoading('member-card')
      } catch (e) {
        setErrorAndStopLoading(e, true)
      }
    })

    Vue.onMounted(() => {
      AuthService.on('authenticated', e => {
        try {
          if (Number.isInteger(e?.detail.loyaltyId)) {
            loyaltyId.value = e.detail.loyaltyId
          } else {
            throw Error('no loyaltyID')
          }
        } catch (e) {
          setErrorAndStopLoading(e, true)
        }
      })
    })

    return {
      loyaltyId,
      createApplePass,
      createGooglePass,
      member,
      isModalOpen,
      memberLevelName,
    }
  },
})

const appElement = document.getElementById('member-card-app')

if (appElement) {
  MemberCardApp.config.globalProperties.$AuthService = AuthService
  MemberCardApp.use(i18n)
  MemberCardApp.mount(appElement)
}

export default MemberCardApp
